import React, { useEffect, useState, useCallback } from 'react';
import { getRequests, reprocessRequest, deleteRequest } from '../services/api';
import { modeBadgeClass } from '../utils/modeUI';

export default function RequestList({ onSelect, modeFilter = '', onModeChange }) {
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stateFilter, setStateFilter] = useState('');
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => { setPage(1); }, [stateFilter, modeFilter, pageSize]);

  const load = useCallback(() => {
    getRequests(page, pageSize, stateFilter, modeFilter)
      .then(data => {
        setRequests(data.requests);
        setPages(data.pages);
        setTotal(data.total);
      })
      .catch(console.error);
  }, [page, pageSize, stateFilter, modeFilter]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div className="request-list-panel">
      {/* Toolbar: mode tabs + state filter + pagination all in one row */}
      <div className="list-toolbar">
        <div className="toolbar-left">
          <div className="mode-filter-tabs">
            {[['', 'All'], ['llm', 'LLM'], ['local-llm', 'Local LLM'], ['vector', 'Vector'], ['hybrid', 'Hybrid'], ['vector-llm', 'Vec+LLM'], ['hybrid-os', 'Hybrid OS'], ['hybrid-os-judge', 'OS Judge']].map(([val, label]) => (
              <button
                key={val}
                className={`mode-filter-tab ${modeFilter === val ? 'active' : ''}`}
                onClick={() => onModeChange(val)}
              >
                {label}
              </button>
            ))}
          </div>
          <select className="state-select" value={stateFilter} onChange={e => setStateFilter(e.target.value)} aria-label="Filter by state">
            <option value="">All States</option>
            <option value="RECEIVED">Received</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
          </select>
          <span className="total-count">{total}</span>
        </div>
        <div className="toolbar-right">
          <select className="state-select" value={pageSize} onChange={e => setPageSize(Number(e.target.value))} aria-label="Rows per page" style={{ width: 'auto', padding: '3px 6px' }}>
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={25}>25 / page</option>
          </select>
          <nav aria-label="Request list pagination" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label="Previous page">Prev</button>
            <span className="page-info" aria-live="polite" aria-atomic="true">{page}/{pages}</span>
            <button className="page-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)} aria-label="Next page">Next</button>
          </nav>
        </div>
      </div>

      {/* Compact table */}
      <div className="data-table-wrap">
        <table className="data-table compact">
          <thead>
            <tr>
              <th scope="col">Request ID</th>
              <th scope="col">State</th>
              <th scope="col">Mode</th>
              <th scope="col">Source</th>
              <th scope="col" title="Expenditures / Candidates">Exp / Cands</th>
              <th scope="col">Award #</th>
              <th scope="col">Category</th>
              <th scope="col">Amount</th>
              <th scope="col">Why</th>
              <th scope="col">Time</th>
            </tr>
          </thead>
          <tbody>
            {requests.map(r => {
              const sourceFile = r.source_files?.expenditure_file?.file_name;
              return (
                <tr
                  key={r.request_id}
                  onClick={() => onSelect(r.request_id)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(r.request_id); } }}
                  tabIndex={0}
                  aria-label={`Request ${r.request_id?.substring(0, 12)}, state ${r.state}`}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="mono" title={r.request_id}>{r.request_id?.substring(0, 12)}...</td>
                  <td>
                    <span className={`badge ${r.state?.toLowerCase()}`}>{r.state}</span>
                    {r.state === 'FAILED' && (
                      <button
                        aria-label={`Retry request ${r.request_id?.substring(0, 12)}`}
                        style={{
                          marginLeft: 6, padding: '2px 8px', borderRadius: 4,
                          border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)',
                          color: '#ef4444', cursor: 'pointer', fontSize: 10, fontWeight: 600,
                        }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await reprocessRequest(r.request_id);
                            load();
                          } catch (err) {
                            alert('Reprocess failed: ' + (err.response?.data?.error || err.message));
                          }
                        }}
                      >
                        Retry
                      </button>
                    )}
                    <button
                      aria-label={`Delete request ${r.request_id?.substring(0, 12)}`}
                      title="Delete this request"
                      style={{
                        marginLeft: 6, padding: '2px 7px', borderRadius: 4,
                        border: '1px solid rgba(100,100,100,0.3)', background: 'transparent',
                        color: 'var(--text-muted)', cursor: 'pointer', fontSize: 10, fontWeight: 600,
                        lineHeight: 1,
                      }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Delete request "${r.request_id}"?\nThis cannot be undone.`)) return;
                        try {
                          await deleteRequest(r.request_id);
                          load();
                        } catch (err) {
                          alert('Delete failed: ' + (err.response?.data?.error || err.message));
                        }
                      }}
                    >
                      <span aria-hidden="true">✕</span>
                    </button>
                  </td>
                  <td>
                    <span className={`badge ${modeBadgeClass(r.matching_mode)}`}>{(r.matching_mode || 'llm').toUpperCase()}</span>
                    {r.group_id && <span style={{ marginLeft: 4, fontSize: 9, color: 'var(--accent-hover)' }} title={`Group: ${r.group_id}`}>&#9679;</span>}
                  </td>
                  <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }} title={sourceFile || ''}>
                    {sourceFile || '-'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 11 }}>
                    <span style={{ color: 'var(--accent-hover)', fontWeight: 600 }}>{r.expenditure_count ?? '-'}</span>
                    <span style={{ color: 'var(--text-muted)', margin: '0 3px' }}>/</span>
                    <span style={{ color: 'var(--text-muted)' }}>{r.candidate_count ?? '-'}</span>
                  </td>
                  <td className="mono">{r.expenditures?.[0]?.awd_num || '-'}{r.expenditures?.length > 1 ? ` (+${r.expenditures.length - 1})` : ''}</td>
                  <td>{r.expenditures?.[0]?.exp_cat || '-'}</td>
                  <td style={{ fontWeight: 600 }}>${r.expenditures?.[0]?.amt?.toLocaleString() || '-'}</td>
                  <td style={{ maxWidth: 250, fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.top_match_reason || ''}>
                    {r.top_match_reason
                      ? <><span className="mono" style={{ color: 'var(--accent-hover)', marginRight: 4 }}>{r.top_match_awd}</span>{r.top_match_reason}</>
                      : r.state === 'COMPLETED' ? <span style={{ color: 'var(--red)' }}>No match</span> : '-'
                    }
                  </td>
                  <td className="mono">{r.processing_time_ms ? `${r.processing_time_ms}ms` : '-'}</td>
                </tr>
              );
            })}
            {requests.length === 0 && (
              <tr><td colSpan={10} className="empty-state" style={{ padding: 16 }} role="status">No requests found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
